// src/modules/requerimiento/views/adicion/TrabajoCampo.jsx
import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Tooltip,
  Card,
  CardContent,
  CardActionArea,
  CardActions,
  LinearProgress,
} from "@mui/material";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import AddIcon from "@mui/icons-material/Add";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  getAdicionAsignados,
  postAsignarPlaca,
  unassignChip,
  updateRequerimiento,
} from "../../services/reqSolicitudes";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import ENV from "../../../../config/env";
import CentroEquipoDetalleDialog from "../../components/CentroEquipoDetalleDialog";
import EquipoInstaladoDialog from "../../components/adicion/EquipoInstaladoDialog";

export default function TrabajoCampo({
  cod,
  placas = [], // ← placas desde RequerimientoDetalle.jsx
  refreshKey,
  onToast,
  onGuardarDatosModem, // opcional: callback al guardar
  apiOrigin = ENV.API_BASE_URL,
  onRefreshRequerimiento,
  showFinalizarInParent = false,
  // Adición sin Programación: un equipo pendiente (aún no instalado) se puede quitar del requerimiento.
  permitirQuitar = false,
  codServicioCliente,
  // Agrega a la coordinación una placa que faltaba; devuelve la placa normalizada (RequerimientoDetalle.jsx).
  onAgregarPlaca,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  // equipo ya instalado abierto en EquipoInstaladoDialog
  const [infoRow, setInfoRow] = useState(null);
  const [centroEquipo, setCentroEquipo] = useState(null);

  const openCentroEquipo = useCallback((identificador, tipo = "modem") => {
    const value = identificador == null ? "" : String(identificador).trim();
    if (!value) return;
    setCentroEquipo({ tipo, identificador: value });
  }, []);

  // formulario modal principal
  const [form, setForm] = useState({
    placa: "",
    alias: "",
    transportista: "",
    grupo: "",
    sociedad: "",
    observacion: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);

  // imagen del modal principal
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [imageError, setImageError] = useState("");

  const toAbsUrl = useCallback(
    (u) => {
      if (!u) return "";
      if (/^https?:\/\//i.test(u)) return u;
      if (apiOrigin) return `${apiOrigin}${u}`;
      return u;
    },
    [apiOrigin]
  );

  const fetchData = useCallback(
    async () => {
      if (!cod) return;
      try {
        setLoading(true);
        setErr(null);
        const { data } = await getAdicionAsignados(cod);
        setItems(Array.isArray(data) ? data : []);
      } catch (e) {
        const msg = e?.response?.data?.error || e?.message || "No se pudo cargar asignados";
        setErr(msg);
        onToast?.({ sev: "error", msg });
      } finally {
        setLoading(false);
      }
    },
    [cod, onToast]
  );

  const isAsignado = (s) => String(s ?? "").trim().toLowerCase() === "asignado";
  const pendientes = items.filter((r) => isAsignado(r?.estado));
  const puedeFinalizar = !loading && !err && items.length > 0 && pendientes.length === 0;
  const [finishing, setFinishing] = useState(false);
  const [quitarRow, setQuitarRow] = useState(null);
  const [quitando, setQuitando] = useState(false);
  const [nuevaPlaca, setNuevaPlaca] = useState("");
  const [nuevaPlacaError, setNuevaPlacaError] = useState("");
  const [agregandoPlaca, setAgregandoPlaca] = useState(false);

  // Placa que no estaba en la coordinación: se agrega ahí y queda elegida.
  const handleAgregarPlaca = async () => {
    const valor = nuevaPlaca.trim();
    if (!valor || agregandoPlaca) return;
    const existente = placas.find((p) => p.toUpperCase() === valor.toUpperCase());
    if (existente) {
      setForm((f) => ({ ...f, placa: existente }));
      setNuevaPlaca("");
      setNuevaPlacaError("");
      return;
    }
    try {
      setAgregandoPlaca(true);
      setNuevaPlacaError("");
      const placa = await onAgregarPlaca(valor);
      setForm((f) => ({ ...f, placa }));
      setNuevaPlaca("");
      onToast?.({ sev: "success", msg: "Placa agregada a la coordinación" });
    } catch (e) {
      setNuevaPlacaError(e?.response?.data?.message || e?.message || "No se pudo agregar la placa");
    } finally {
      setAgregandoPlaca(false);
    }
  };

  const handleConfirmQuitar = async () => {
    if (!quitarRow?.num_sim) return;
    try {
      setQuitando(true);
      await unassignChip(cod, quitarRow.num_sim, { actor: "system", cod_servicio_cliente: codServicioCliente ?? null });
      onToast?.({ sev: "success", msg: "Equipo quitado del requerimiento" });
      setQuitarRow(null);
      await onRefreshRequerimiento?.();
      await fetchData();
    } catch (e) {
      onToast?.({ sev: "error", msg: e?.response?.data?.message || e?.message || "No se pudo quitar el equipo" });
    } finally {
      setQuitando(false);
    }
  };

  const handleFinalizar = async () => {
    try {
      const ok = window.confirm("¿Finalizar trabajo de campo? Pasará a 'Revisión en Sistemas'.");
      if (!ok) return;
      setFinishing(true);
      await updateRequerimiento(cod, { estado: 7 });
      await onRefreshRequerimiento?.();
      onToast?.({ sev: "success", msg: "Actualizado a 'Revisión en Sistemas'" });
      await fetchData();
    } catch (e) {
      onToast?.({
        sev: "error",
        msg: e?.response?.data?.message || e?.message || "No se pudo finalizar",
      });
    } finally {
      setFinishing(false);
    }
  };

  const handleItemClick = (row) => {
    if (isAsignado(row?.estado)) {
      handleOpen(row); // formulario (asignación)
    } else {
      handleOpenInfo(row); // datos actuales
    }
  };

  const handleOpenInfo = (row) => setInfoRow(row);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  useEffect(() => {
    return () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
    };
  }, [imagePreview]);

  const empty = !loading && !err && items.length === 0;

  const handleOpen = (row) => {
    setSelected(row);
    setNuevaPlaca("");
    setNuevaPlacaError("");
    setForm({
      placa: "",
      alias: row?.alias || "",
      transportista: row?.transportista || "",
      grupo: row?.grupo || "",
      sociedad: row?.sociedad || "",
      observacion: "",
    });
    setImageFile(null);
    setImagePreview("");
    setImageError("");
    setOpen(true);
  };

  const handleClose = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setOpen(false);
    setSelected(null);
    setSubmitted(false);
    setImageFile(null);
    setImagePreview("");
    setImageError("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleSave = async () => {
    setSubmitted(true);
    if (!form.placa) {
      onToast?.({ sev: "warning", msg: "Selecciona una placa para continuar." });
      return;
    }
    if (!selected?.num_sim) {
      onToast?.({ sev: "error", msg: "No se encontró el número de SIM del ítem seleccionado." });
      return;
    }
    try {
      setSaving(true);
      const payload = {
        placa: form.placa,
        alias: form.alias,
        transportista: form.transportista,
        grupo: form.grupo,
        sociedad: form.sociedad,
        observacion: form.observacion,
        imageFile,
      };
      if (typeof onGuardarDatosModem === "function") {
        await onGuardarDatosModem({
          cod,
          num_sim: selected.num_sim,
          emai: selected.emai,
          ...form,
          imageFile,
        });
      } else {
        await postAsignarPlaca(cod, selected.num_sim, payload);
      }
      await fetchData();
      onToast?.({ sev: "success", msg: "Datos guardados" });
      handleClose();
    } catch (e) {
      onToast?.({ sev: "error", msg: e?.message || "No se pudo guardar" });
    } finally {
      setSaving(false);
    }
  };

  const placaError = submitted && !form.placa;

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setImageError("Solo se permiten archivos de imagen.");
      setImageFile(null);
      setImagePreview("");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setImageError("La imagen no debe superar los 5 MB.");
      setImageFile(null);
      setImagePreview("");
      return;
    }
    setImageError("");
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleClearImage = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(null);
    setImagePreview("");
    setImageError("");
  };

  /*
  const handleInfoImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setInfoImageError("Solo se permiten imágenes.");
      setInfoImageFile(null);
      setInfoImagePreview("");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setInfoImageError("La imagen no debe superar los 5 MB.");
      setInfoImageFile(null);
      setInfoImagePreview("");
      return;
    }
    setInfoImageError("");
    setInfoImageFile(file);
    const url = URL.createObjectURL(file);
    setInfoImagePreview(url);
  };
  */

  /*
  const handleInfoClearImage = () => {
    if (infoImagePreview) URL.revokeObjectURL(infoImagePreview);
    setInfoImageFile(null);
    setInfoImagePreview("");
    setInfoImageError("");
  };

  const handleInfoSaveImage = async () => {
    if (!infoRow) return;
    if (!infoImageFile) {
      setInfoImageError("Selecciona una imagen primero.");
      return;
    }
    try {
      setInfoSavingImage(true);
      // usamos el mismo endpoint que usas en el formulario principal
      await postAsignarPlaca(cod, infoRow.num_sim, {
        placa: infoRow.placa || "",
        alias: infoRow.alias || "",
        transportista: infoRow.transportista || "",
        sociedad: infoRow.sociedad || "",
        observacion: infoRow.observacion || "",
        imageFile: infoImageFile,
      });
      onToast?.({ sev: "success", msg: "Imagen guardada" });
      await fetchData();
      handleCloseInfo();
    } catch (err) {
      onToast?.({
        sev: "error",
        msg: err?.response?.data?.message || err?.message || "No se pudo guardar la imagen",
      });
    } finally {
      setInfoSavingImage(false);
    }
  };
  */

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
      {items.length > 0 && (() => {
        const instalados = Math.max(items.length - pendientes.length, 0);
        const listo = instalados >= items.length;
        const color = listo ? "success" : "warning";
        return (
          <Card variant="outlined" sx={{ mb: 2, borderRadius: "10px" }}>
            <CardContent sx={{ py: 1.75, "&:last-child": { pb: 1.75 } }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {instalados} de {items.length} equipos instalados
                </Typography>
                {listo && <CheckCircleIcon color="success" />}
              </Stack>
              <LinearProgress
                variant="determinate"
                color={color}
                value={(instalados / items.length) * 100}
                sx={{ height: 8, borderRadius: 99, my: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                {listo
                  ? "Listo: ya puedes finalizar el trabajo de campo."
                  : "Usa \"Instalar en vehículo\" en cada equipo pendiente para indicar dónde se instaló."}
              </Typography>
            </CardContent>
          </Card>
        );
      })()}

      {loading && (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Cargando asignados...</Typography>
          </CardContent>
        </Card>
      )}

      {err && !loading && (
        <Card variant="outlined" sx={{ borderRadius: 2, borderColor: "error.light" }}>
          <CardContent sx={{ py: 2 }}>
            <Typography variant="body2" color="error">
              {err}
            </Typography>
          </CardContent>
        </Card>
      )}

      {empty && (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent sx={{ py: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Aun no hay unidades asignadas para este requerimiento.
            </Typography>
          </CardContent>
        </Card>
      )}

      {!loading && !err && items.length > 0 && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(2, minmax(0, 1fr))",
            },
            gap: 1.5,
          }}
        >
          {items.map((row) => {
            const numSim = row?.num_sim ?? "-";
            const emai = row?.emai ?? "—";
            const ip = row?.ip ?? "—";
            const modelo = row?.modelo ?? "—";
            const placa = row?.placa ?? "—";
            const chips = getChips(row);
            const inicial =
              String(row?.asignado || row?.ciudad || numSim || "?")
                .trim()
                .charAt(0)
                .toUpperCase() || "?";

            return (
              <Card
                key={`${row.id_historial}-${numSim}`}
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  borderColor: isAsignado(row?.estado) ? "warning.light" : "success.light",
                  transition: "box-shadow 120ms ease, border-color 120ms ease",
                  "&:hover": {
                    boxShadow: 2,
                    borderColor: isAsignado(row?.estado) ? "warning.main" : "success.main",
                  },
                }}
              >
                <CardActionArea onClick={() => handleItemClick(row)}>
                  <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                    <Stack spacing={1.25}>
                      <Stack direction="row" alignItems="flex-start" spacing={1.25}>
                        <Avatar
                          sx={{
                            width: 36,
                            height: 36,
                            bgcolor: isAsignado(row?.estado) ? "warning.light" : "success.light",
                            color: isAsignado(row?.estado) ? "warning.dark" : "success.dark",
                            fontWeight: 700,
                          }}
                        >
                          {inicial}
                        </Avatar>

                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, wordBreak: "break-word" }}>
                              {numSim}
                            </Typography>
                            {isAsignado(row?.estado) ? (
                              <Tooltip title="Pendiente">
                                <HourglassEmptyIcon fontSize="small" sx={{ color: "warning.main" }} />
                              </Tooltip>
                            ) : (
                              <Tooltip title="OK - Ver datos actuales">
                                <CheckCircleIcon fontSize="small" sx={{ color: "success.main" }} />
                              </Tooltip>
                            )}
                          </Stack>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, wordBreak: "break-word" }}>
                            {modelo}
                          </Typography>
                        </Box>
                      </Stack>

                      <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                        <Chip size="small" color="primary" label={`Placa: ${placa}`} sx={{ fontWeight: 700 }} />
                        {row?.estado && (
                          <Chip
                            size="small"
                            color={isAsignado(row.estado) ? "warning" : "success"}
                            variant="outlined"
                            label={row.estado}
                          />
                        )}
                        {row?.ciudad && <Chip size="small" variant="outlined" label={row.ciudad} />}
                      </Stack>

                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                          Chips
                        </Typography>
                        <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                          {chips.length > 0 ? (
                            chips.map((chip, chipIdx) => (
                              <Chip
                                key={`${chip?.id_historial ?? chip?.numero ?? chipIdx}-${chipIdx}`}
                                size="small"
                                variant="outlined"
                                label={getChipNumber(chip)}
                              />
                            ))
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Sin chips asignados
                            </Typography>
                          )}
                        </Stack>
                      </Box>

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
                          gap: 1,
                          pt: 0.5,
                        }}
                      >
                        <Field label="Num SIM" value={numSim} />
                        <Field label="IMEI/EMAI" value={emai} />
                        <Field label="Origen/IP" value={ip} />
                      </Box>
                    </Stack>
                  </CardContent>
                </CardActionArea>
                {isAsignado(row?.estado) && (
                  <CardActions sx={{ px: 1.5, pb: 1.5, pt: 0, gap: 1 }}>
                    <Button
                      variant="contained"
                      startIcon={<DirectionsCarIcon />}
                      onClick={() => handleItemClick(row)}
                      sx={{ flex: 1, textTransform: "none", fontWeight: 700 }}
                    >
                      Instalar en vehículo
                    </Button>
                    {permitirQuitar && (
                      <Tooltip title="Quitar del requerimiento">
                        <Button
                          color="error"
                          variant="outlined"
                          startIcon={<LinkOffIcon />}
                          onClick={() => setQuitarRow(row)}
                          disabled={!row?.num_sim}
                          sx={{ textTransform: "none" }}
                        >
                          Quitar
                        </Button>
                      </Tooltip>
                    )}
                  </CardActions>
                )}
              </Card>
            );
          })}
        </Box>
      )}

      <Dialog open={!!quitarRow} onClose={() => !quitando && setQuitarRow(null)} fullWidth maxWidth="xs">
        <DialogTitle>Quitar equipo</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            ¿Seguro que quieres quitar el equipo <strong>{quitarRow?.emai || quitarRow?.num_sim || "—"}</strong> del requerimiento?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuitarRow(null)} disabled={quitando}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleConfirmQuitar} disabled={quitando}>
            {quitando ? "Quitando…" : "Quitar"}
          </Button>
        </DialogActions>
      </Dialog>

      {puedeFinalizar && !showFinalizarInParent && (
        <Card
          variant="outlined"
          sx={{
            position: "sticky",
            bottom: 0,
            mt: 2,
            bgcolor: "background.paper",
            borderRadius: 2,
            zIndex: 1,
          }}
        >
          <CardContent
            sx={{
              py: 1.25,
              display: "flex",
              justifyContent: "flex-end",
              "&:last-child": { pb: 1.25 },
            }}
          >
            <Button variant="contained" color="success" onClick={handleFinalizar} disabled={finishing}>
              {finishing ? "Finalizando…" : "Finalizar trabajo de campo"}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* MODAL CON FORMULARIO */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>
        {selected?.num_sim ? (
          <>
            Modem:{" "}
            <Box
              component="span"
              sx={{
                cursor: "pointer",
                color: "primary.main",
                textDecoration: "underline",
              }}
              onClick={() => openCentroEquipo(selected.num_sim)}
            >
              {selected.num_sim}
            </Box>
          </>
        ) : (
          "Detalle de módem asignado"
        )}
      </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2}>
            <FormControl fullWidth size="small" required error={placaError}>
              <InputLabel id="placa-label" required shrink>
                Placa
              </InputLabel>
              <Select
                labelId="placa-label"
                label="Placa"
                name="placa"
                value={form.placa}
                onChange={handleChange}
                displayEmpty
                notched
                renderValue={(val) =>
                  val ? val : <em style={{ opacity: 0.7 }}>Selecciona una placa…</em>
                }
              >
                <MenuItem value="" disabled>
                  <em>Selecciona una placa…</em>
                </MenuItem>
                {placas.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </Select>
              {placaError && <FormHelperText>La placa es obligatoria.</FormHelperText>}
              {!placaError && placas.length === 0 && (
                <FormHelperText>La coordinación no tiene placas{onAgregarPlaca ? "; agrega una abajo." : "."}</FormHelperText>
              )}
            </FormControl>

            {onAgregarPlaca && (
              <Box>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <TextField
                    size="small"
                    fullWidth
                    label="¿No está la placa? Escríbela"
                    placeholder="1234-ABC"
                    value={nuevaPlaca}
                    onChange={(e) => { setNuevaPlaca(e.target.value.toUpperCase()); setNuevaPlacaError(""); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAgregarPlaca(); } }}
                    error={!!nuevaPlacaError}
                    helperText={nuevaPlacaError || "Se agrega a la coordinación y queda elegida."}
                    disabled={agregandoPlaca || saving}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleAgregarPlaca}
                    disabled={!nuevaPlaca.trim() || agregandoPlaca || saving}
                    startIcon={agregandoPlaca ? <CircularProgress size={16} /> : <AddIcon />}
                    sx={{ flexShrink: 0, height: 40, textTransform: "none" }}
                  >
                    Agregar
                  </Button>
                </Stack>
              </Box>
            )}

            <TextField
              size="small"
              label="Alias"
              name="alias"
              value={form.alias}
              onChange={handleChange}
              fullWidth
            />

            <TextField
              size="small"
              label="Transportista"
              name="transportista"
              value={form.transportista}
              onChange={handleChange}
              fullWidth
            />

            <TextField
              size="small"
              label="Grupo"
              name="grupo"
              value={form.grupo}
              onChange={handleChange}
              fullWidth
            />

            <TextField
              size="small"
              label="Sociedad"
              name="sociedad"
              value={form.sociedad}
              onChange={handleChange}
              fullWidth
            />

            <TextField
              size="small"
              label="Observación"
              name="observacion"
              value={form.observacion}
              onChange={handleChange}
              fullWidth
              multiline
              minRows={3}
            />

            {/* Imagen (opcional) */}
            <Box>
              <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
                Imagen (opcional)
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                <Button variant="outlined" startIcon={<PhotoCameraIcon />} component="label">
                  Elegir imagen
                  <input type="file" accept="image/*" hidden onChange={handleImageChange} />
                </Button>
                {imagePreview && (
                  <Button
                    variant="text"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={handleClearImage}
                  >
                    Quitar
                  </Button>
                )}
              </Stack>
              {imageError && (
                <FormHelperText error sx={{ mt: 0.5 }}>
                  {imageError}
                </FormHelperText>
              )}
              {imagePreview && (
                <Box
                  sx={{
                    mt: 1.5,
                    borderRadius: 2,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <img
                    src={imagePreview}
                    alt="preview"
                    style={{ display: "block", width: "100%", height: "auto" }}
                  />
                </Box>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={saving}>
            Cerrar
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || agregandoPlaca || !!imageError || placas.length === 0}
          >
            {saving ? "Guardando…" : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODAL: EQUIPO YA INSTALADO */}
      <EquipoInstaladoDialog
        row={infoRow}
        cod={cod}
        onClose={() => setInfoRow(null)}
        onSaved={async (actualizado) => {
          setInfoRow((prev) => (prev ? { ...prev, ...actualizado } : prev));
          await fetchData();
        }}
        onToast={onToast}
        onVerEquipo={(numSim) => openCentroEquipo(numSim)}
        toAbsUrl={toAbsUrl}
      />

      <CentroEquipoDetalleDialog
        open={!!centroEquipo}
        tipo={centroEquipo?.tipo || "modem"}
        identificador={centroEquipo?.identificador}
        onClose={() => setCentroEquipo(null)}
      />
    </Box>
  );
}

function getChips(row) {
  return Array.isArray(row?.chips) ? row.chips : [];
}

function getChipNumber(chip) {
  return chip?.numero || chip?.num_sim || chip?.chip || "-";
}

function Field({ label, value }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-word" }}>
        {value}
      </Typography>
    </Box>
  );
}
